import { useEffect, useRef } from 'react';
import './App.css';

const whatsappUrl = 'https://wa.me/6591889109';
const instagramUrl = 'https://www.instagram.com/thepianogardensg/?hl=en';
const musicUrl = '/assets/audios/ravel.mp3';

const sections = [
  {
    title: 'Achievements',
    text: 'Prize moments that honour patient practice, brave performances, and the quiet work behind every polished piece.',
    image: '/assets/images/instagram/blue-trophies.jpg',
  },
  {
    title: 'Concerts',
    text: 'Concert days where students gather, perform with poise, and celebrate music with family and friends.',
    image: '/assets/images/instagram/recital-performance.jpg',
  },
  {
    title: 'Festivals and Competitions',
    text: 'Wider stages and shared experiences that help young musicians listen, travel, compete, and grow.',
    image: '/assets/images/instagram/award-group.jpg',
  },
];

function App() {
  const audioRef = useRef(null);

  const playMusic = async () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(musicUrl);
      audioRef.current.loop = true;
      audioRef.current.volume = 0.22;
      audioRef.current.preload = 'none';
    }

    try {
      await audioRef.current.play();
      return true;
    } catch (_error) {
      // Browsers may block unmuted autoplay until the visitor interacts with the page.
      return false;
    }
  };

  useEffect(() => {
    let waitingForInteraction = false;
    const interactionEvents = ['pointerdown', 'touchstart', 'keydown'];

    const removeInteractionListeners = () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, playAfterInteraction);
      });
    };

    const playAfterInteraction = async () => {
      removeInteractionListeners();
      waitingForInteraction = false;
      await playMusic();
    };

    const addInteractionListeners = () => {
      if (waitingForInteraction) {
        return;
      }

      waitingForInteraction = true;
      interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, playAfterInteraction, { once: true });
      });
    };

    const startAfterLoad = () => {
      const idleCallback = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 900));
      idleCallback(async () => {
        const isPlaying = await playMusic();
        if (!isPlaying) {
          addInteractionListeners();
        }
      });
    };

    if (document.readyState === 'complete') {
      startAfterLoad();
      return undefined;
    }

    window.addEventListener('load', startAfterLoad, { once: true });
    return () => {
      window.removeEventListener('load', startAfterLoad);
      removeInteractionListeners();
    };
  }, []);

  useEffect(() => {
    const revealItems = [...document.querySelectorAll('.reveal')];

    if (!('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.18 },
    );

    revealItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="site">
      <header className="nav" aria-label="Main navigation">
        <a className="brand" href="#top" aria-label="The Piano Garden home">
          <img src="/assets/logo/piano-garden-mark.png" alt="" />
          <span>The Piano Garden</span>
        </a>
        <a className="contact-link" href={instagramUrl} target="_blank" rel="noreferrer">
          Instagram
        </a>
      </header>

      <section className="hero" id="top">
        <div className="hero-copy reveal is-visible">
          <h1>The Piano Garden</h1>
          <p className="intro">Where music blossoms.</p>
          <a className="button" href={whatsappUrl} target="_blank" rel="noreferrer">
            Enquire
          </a>
        </div>
        <figure className="hero-image reveal is-visible">
          <img src="/assets/images/instagram/studio-piano.webp" alt="The Piano Garden studio piano" />
        </figure>
      </section>

      <section className="statement reveal" aria-label="Studio values">
        <p>Play with grace. Learn with joy. Grow with music.</p>
      </section>

      <section className="sections" aria-label="Highlights">
        {sections.map((section, index) => (
          <article className="feature reveal" style={{ '--reveal-delay': `${index * 90}ms` }} key={section.title}>
            <img src={section.image} alt="" />
            <div>
              <h2>{section.title}</h2>
              <p>{section.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="closing reveal">
        <a className="button button-dark" href={whatsappUrl} target="_blank" rel="noreferrer">
          Enquire
        </a>
      </section>
    </main>
  );
}

export default App;
